import { observer } from 'mobx-react-lite'
import { useEffect } from 'react'
import { useHistory } from 'react-router-dom'
import { Box, Text, Group, Button, Skeleton, Stack } from '@mantine/core'
import { format, isToday, isThisWeek } from 'date-fns'
import { useStore } from '../../app/stores/store'
import { EmptyState } from '../../app/common/ui'
import type { NotificationItem, NotificationType } from '../../app/stores/notificationStore'

const TYPE_COLORS: Record<NotificationType | string, string> = {
  NewFollower: '#58A6FF',
  PostUpvoted: '#E85D04',
  CommentOnPost: '#3FB950',
  NewComment: '#3FB950',
  VoteMilestone: '#C9A84C',
  WishlistAdd: '#58A6FF',
  PartApproved: '#3FB950',
  PartRejected: '#F85149',
  ModelApproved: '#3FB950',
  ModelRejected: '#F85149',
  FlagResolved: '#D29922',
  ReputationChange: '#C9A84C'
}

const MESSAGE_LINE_SX = {
  fontFamily: 'DM Sans, sans-serif',
  fontSize: 12,
  lineHeight: 1.45,
  color: 'var(--gs-text)',
  wordBreak: 'break-word' as const
}

const BTN_STYLE = {
  background: '#21262D',
  border: '1.5px solid #484F58',
  color: '#E6EDF3',
  padding: '6px 14px',
  borderRadius: 5,
  fontSize: 12,
  fontFamily: 'DM Sans, sans-serif',
  cursor: 'pointer' as const
}

function getSection (date: Date): 'TODAY' | 'THIS WEEK' | 'EARLIER' {
  if (isToday(date)) return 'TODAY'
  if (isThisWeek(date, { weekStartsOn: 0 })) return 'THIS WEEK'
  return 'EARLIER'
}

function groupBySection (items: NotificationItem[]): { section: 'TODAY' | 'THIS WEEK' | 'EARLIER'; items: NotificationItem[] }[] {
  const groups: Record<string, NotificationItem[]> = { TODAY: [], 'THIS WEEK': [], EARLIER: [] }
  const order: ('TODAY' | 'THIS WEEK' | 'EARLIER')[] = ['TODAY', 'THIS WEEK', 'EARLIER']
  items.forEach((n) => {
    const section = getSection(new Date(n.createdAt))
    groups[section].push(n)
  })
  return order.filter((s) => groups[s].length > 0).map((section) => ({ section, items: groups[section] }))
}

function getNavigatePath (item: NotificationItem): string | null {
  const t = item.targetType
  const id = item.targetId
  const username = item.actorUsername
  if (t === 'Post' && id) return `/blog/${id}`
  if (t === 'Part') return '/parts-bin'
  if (t === 'FirearmModel' && id) return `/registry/${id}`
  if (t === 'Build' && id) return `/builds/${id}`
  if (t === 'Follow' && username) return `/profiles/${username}`
  return null
}

export default observer(function NotificationsPage () {
  const { notificationStore } = useStore()
  const history = useHistory()

  useEffect(() => {
    notificationStore.loadNotifications({ pageNumber: 1, pageSize: 20 })
  }, [notificationStore])

  const handleItemClick = (item: NotificationItem) => {
    notificationStore.markRead([item.id])
    const path = getNavigatePath(item)
    if (path) history.push(path)
  }

  const grouped = groupBySection(notificationStore.notifications)
  const isEmpty = notificationStore.notifications.length === 0
  const showInitialLoading = notificationStore.loading && isEmpty

  return (
    <Box
      sx={{
        maxWidth: 800,
        margin: '0 auto',
        padding: '16px 24px'
      }}
    >
      <Group position="apart" mb="md">
        <Group spacing="sm">
          <Text sx={{ fontFamily: 'Oswald, sans-serif', fontWeight: 600, fontSize: 20, color: 'var(--gs-text)' }}>
            NOTIFICATIONS
          </Text>
          {notificationStore.unreadCount > 0 && (
            <Box
              sx={{
                background: 'var(--gs-red)',
                color: '#fff',
                fontSize: 11,
                fontWeight: 700,
                padding: '2px 8px',
                borderRadius: 12,
                minWidth: 20,
                textAlign: 'center'
              }}
            >
              {notificationStore.unreadCount > 99 ? '99+' : notificationStore.unreadCount}
            </Box>
          )}
        </Group>
        <Box
          component="button"
          type="button"
          onClick={() => notificationStore.markAllRead()}
          sx={BTN_STYLE}
        >
          Mark all read
        </Box>
      </Group>

      {showInitialLoading
        ? (
        <Stack spacing="sm" mt="md">
          {[1, 2, 3, 4, 5].map((k) => (
            <Group key={k} spacing="md" noWrap align="flex-start">
              <Skeleton height={36} width={36} circle />
              <Box sx={{ flex: 1 }}>
                <Skeleton height={12} mb={8} />
                <Skeleton height={10} width="40%" />
              </Box>
            </Group>
          ))}
        </Stack>
          )
        : isEmpty
          ? (
        <EmptyState
          title="No notifications yet"
          message="Follow users and engage with content to see activity here."
        />
            )
          : (
        <>
          {grouped.map(({ section, items }) => (
            <Box key={section}>
              <Text
                sx={{
                  fontFamily: 'DM Sans, sans-serif',
                  fontSize: 10,
                  color: '#8B949E',
                  letterSpacing: 1.5,
                  textTransform: 'uppercase',
                  margin: '14px 0 8px'
                }}
              >
                {section}
              </Text>
              {items.map((item) => {
                const bgColor = TYPE_COLORS[item.type ?? ''] ?? '#8B949E'
                return (
                  <Box
                    key={item.id}
                    onClick={() => handleItemClick(item)}
                    sx={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 12,
                      padding: '12px 8px',
                      borderBottom: '1px solid var(--gs-border)',
                      cursor: 'pointer',
                      borderRadius: 6,
                      ...(item.isRead ? {} : { background: 'rgba(232,93,4,0.04)' })
                    }}
                  >
                    <Box
                      sx={{
                        width: 36,
                        height: 36,
                        borderRadius: '50%',
                        background: bgColor,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: 12,
                        fontWeight: 700,
                        color: '#fff',
                        fontFamily: 'DM Sans, sans-serif',
                        flexShrink: 0
                      }}
                    >
                      {item.actorInitials}
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Box component="div" sx={MESSAGE_LINE_SX}>
                        {item.actorUsername
                          ? item.message.split(new RegExp(`(${escapeRegExp(item.actorUsername)})`, 'gi')).map((part, i) =>
                            part.toLowerCase() === item.actorUsername.toLowerCase()
                              ? (
                                <Box key={i} component="span" sx={{ fontWeight: 700, color: '#E85D04' }}>
                                  {part}
                                </Box>
                                )
                              : (
                                <Box key={i} component="span" sx={{ fontWeight: 400 }}>
                                  {part}
                                </Box>
                                )
                          )
                          : item.message}
                      </Box>
                      <Text size="xs" sx={{ fontFamily: 'DM Sans, sans-serif', fontSize: 10, color: '#8B949E', marginTop: 2, lineHeight: 1.4 }}>
                        {format(new Date(item.createdAt), 'MMM d, h:mm a')}
                      </Text>
                    </Box>
                    <Box
                      sx={{
                        width: 11,
                        minHeight: 36,
                        flexShrink: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                      aria-hidden
                    >
                      {!item.isRead
                        ? (
                        <Box
                          sx={{
                            width: 7,
                            height: 7,
                            borderRadius: '50%',
                            background: '#E85D04',
                            flexShrink: 0
                          }}
                        />
                          )
                        : null}
                    </Box>
                  </Box>
                )
              })}
            </Box>
          ))}

          {notificationStore.hasMore && (
            <Button
              variant="outline"
              mt="lg"
              loaderPosition="right"
              sx={{ borderColor: 'var(--gs-border)', color: 'var(--gs-text)', minWidth: 148 }}
              onClick={() =>
                notificationStore.loadNotifications({
                  pageNumber: notificationStore.pageNumber + 1,
                  pageSize: 20
                })
              }
              loading={notificationStore.loading}
            >
              Load more
            </Button>
          )}
        </>
            )}
    </Box>
  )
})

function escapeRegExp (s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
}
